import React, { useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, Icon } from '@hydrodigital/design-system';
import ClockSvg from '@/assets/svg/ClockSvg';
import type { AlertCardType } from '@/domain/Notifications/Notifications';
import { CARD_CONFIG, resolveCardType } from '../constants';
import type { CardType } from '../types';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

interface BaseCardProps {
	readonly act: AlertCardType & { onDismiss?: () => void };
	readonly titleContent: React.ReactNode;
	readonly messageContent?: React.ReactNode;
	readonly alwaysVisibleContent?: React.ReactNode;
	readonly expandedContent?: React.ReactNode;
}

export default function BaseCard({ act, titleContent, messageContent, alwaysVisibleContent, expandedContent }: BaseCardProps) {
	const [expanded, setExpanded] = useState(false);

	const typeCard: CardType = resolveCardType(act);
	const cardConfig = CARD_CONFIG[typeCard];

	const secInfo = localStorage.getItem('security');
	const tzOffset = Number(JSON.parse(secInfo || 'null')?.timeZone ?? 0);
	const dt = dayjs(act.datetime, 'YYYY/MM/DD hh:mmA').utcOffset(tzOffset * 60, true);
	const userNow = dayjs().utcOffset(tzOffset * 60);
	const dateLabel = dt.isValid() ? `${dt.format('YYYY-MM-DD hh:mma')} (${dt.from(userNow)})` : '';

	return (
		<div
			style={{
				border: `1.5px solid ${cardConfig.borderColor}`,
				background: cardConfig.cardBg,
				borderRadius: 12,
				padding: '14px 16px',
				marginBottom: 14,
				position: 'relative',
				width: '100%',
			}}
		>
			{/* Dismiss button */}
			{act.onDismiss && act.dispatchId && (
				<CloseCircleOutlined
					onClick={(e) => {
						e.stopPropagation();
						act.onDismiss?.();
					}}
					style={{
						position: 'absolute',
						top: 13,
						right: 14,
						fontSize: 16,
						cursor: 'pointer',
						color: '#8C8C8C',
					}}
					aria-label="Dismiss notification"
					title="Dismiss"
				/>
			)}

			{/* Title row */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, color: '#1A1A2E', paddingRight: 28 }}>
				{cardConfig.icon}
				{titleContent}
			</div>

			{/* Message */}
			{messageContent && (
				<div style={{ fontSize: 13, fontWeight: 500, color: '#333', margin: '8px 0 6px 0', lineHeight: 1.5 }}>
					{messageContent}
				</div>
			)}

			{/* Always visible content (strategy name, etc.) */}
			{alwaysVisibleContent}

			{/* Datetime */}
			{dateLabel && (
				<div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0', color: '#555', fontSize: 13, fontWeight: 600 }}>
					<Icon children={<ClockSvg />} style={{ fontSize: 12 }} />
					<span>{dateLabel}</span>
				</div>
			)}

			{/* Expanded details */}
			{expanded && expandedContent}

			{/* View more / View less toggle */}
			<Button
				onClick={() => setExpanded((v) => !v)}
				iconName={expanded ? 'angle-up' : 'angle-down'}
				showIcon
				showLabel
				size="minimal"
				variant="primary"
				style={{
					display: 'flex',
					alignItems: 'center',
					marginTop: 10,
					cursor: 'pointer',
					userSelect: 'none',
				}}
				label={expanded ? 'View less' : 'View more'}
			/>
		</div>
	);
}
